// platform
import { useEffect } from 'react';
import { atomWithImmer } from 'jotai-immer';
import { useAtom } from 'jotai';

// app
import validSvc from '../../../core/validSvc';
import apiSvc from '../../../core/services/apiSvc';
import sessionRepo from '../../../core/repo/sessionRepo';

// types
import { Purl, PurlActive, PurlType, SavedPurlsData, SavedPurlsStore } from './types';
import { ngDelegate } from '../../../core/services/ng.delegate';

// STORE DATA
function modelText(data, propName: string, defaultVal: string = ''): string {
    return validSvc.isText(data[propName]) ? data[propName] : defaultVal;
}

function purlVM(data?): Purl {
    data = data || {};
    return {
        name: modelText(data, 'name'),
        type: modelText(data, 'type', PurlType.NONE) as PurlType,
        json: data.json,
        response: data.response,
    };
}
function purlActiveVM(data?: Purl): PurlActive {
    return {
        name: !!data ? data.name : '',
        type: !!data ? data.type : PurlType.NONE,
        json: !!data ? JSON.stringify(data.json, undefined, 4) : '',
        response: !!data ? JSON.stringify(data.response, undefined, 4) : '',
    };
}

const storeDataVM = (): SavedPurlsData => {
    return {
        items: [],
        activeItem: purlActiveVM(),
    };
};
const savedPurlsAtom = atomWithImmer<SavedPurlsData>(storeDataVM());

// VALIDATE
function purlValid(purlType: PurlType, m: PurlActive): string {
    if (purlType == PurlType.NONE) {
        return 'Purl type must be SAVE or MOCK';
    }
    if (!validSvc.isText(m.name) || !validSvc.isText(m.json) || !validSvc.isJson(m.json)) {
        return 'Purl and valid Json post required';
    }
    if (purlType == PurlType.MOCK && (!validSvc.isText(m.response) || !validSvc.isJson(m.response))) {
        return 'Purl MOCK response must be valid JSON';
    }
    return 'ok';
}

// STORE HOOK
const useSavedPurlsStore = (): SavedPurlsStore => {
    const [store, setStore] = useAtom(savedPurlsAtom);

    // INIT
    useEffect(() => {
        console.log('PURLS STORE: INIT items: ' + store.items.length);
        return (): void => {
            console.log('PURLS STORE: DESTROY with items: ' + store.items.length);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // DEFINITION
    return {
        items: store.items,
        activeItem: store.activeItem,
        setList,
        setActive,
        changeActiveProp,
        saveActive,
        postActive,
        remove,
        unload,
    };

    function setList(): void {
        // load saved purls from local storage

        let purls = sessionRepo.getLocalStoreKey('purls') as Array<Purl>;
        const result: Array<Purl> = [];
        let item: Purl;
        purls = Array.isArray(purls) ? purls : [];

        for (let i = 0; i < purls.length; i++) {
            item = purls[i];
            if (item.type == 'MOCK' || item.type == 'SAVE') {
                result.push(item);
            }
        }

        setStore(draft => {
            draft.items = result;
        });
    }

    function setActive(purl: Purl): void {
        console.log('store with active purl: ' + purl.name);
        setStore(draft => {
            draft.activeItem = purlActiveVM(purl);
        });
    }

    function remove(name: string): void {
        console.log('store delete purl: ' + name);
        if (!!store.items.find(purl => purl.name == name)) {
            const purls = store.items.filter(purl => purl.name != name);
            sessionRepo.setLocalStoreKey('purls', purls);

            setStore(draft => {
                draft.items = purls;
            });
        }
    }

    function saveActive(purlType: PurlType): void {
        const purlOk = purlValid(purlType, store.activeItem);
        if (purlOk != 'ok') {
            ngDelegate.ok('Cannot Save', purlOk);
            return;
        }

        // create saved purl
        const purl = store.activeItem;
        const savePurl = purlVM({
            name: purl.name,
            type: purlType,
            json: JSON.parse(purl.json),
            response: purlType == PurlType.MOCK ? JSON.parse(purl.response) : {},
        });

        const purls = [...store.items]; // de-ref
        const idx = purls.findIndex(f => f.name == purl.name);
        if (idx > -1) {
            purls[idx] = savePurl;
        } else {
            purls.push(savePurl);
        }
        sessionRepo.setLocalStoreKey('purls', purls);

        setStore(draft => {
            draft.items = purls;
        });
    }

    function changeActiveProp(val: any, propName: string): void {
        // change activeItem properties as user edits
        console.log('store change active prop: ' + propName);

        // verify purl has this property name
        if (!!store.activeItem.hasOwnProperty(propName)) {
            setStore(draft => {
                draft.activeItem[propName] = val;
            });
        }
    }

    // POST TO API
    async function apiPost(purl: string, json: string): Promise<void> {
        try {
            const jsonObj = JSON.parse(json);
            const r = await apiSvc.post(purl, jsonObj, true);
            const httpStatus = validSvc.isInteger(r.status) ? r.status.toString() : '200';
            setStore(draft => {
                draft.activeItem.response = JSON.stringify(r, null, 4);
                draft.activeItem.status = httpStatus;
            });
        } catch (err: any) {
            if (!!err) {
                ngDelegate.ok('Post Error');
                setStore(draft => {
                    draft.activeItem.response = JSON.stringify(err, null, 4);
                    draft.activeItem.status = 'ERR';
                });
            }
        }
    }
    function postActive(): void {
        // post the active purl to api
        const purl = store.activeItem;
        if (!validSvc.isText(purl.name) || !validSvc.isText(purl.json) || !validSvc.isJson(purl.json)) {
            ngDelegate.ok('Cannot Post', 'Purl and valid Json post required');
            return;
        }
        setStore(draft => {
            draft.activeItem.response = '';
        });

        // ASYNC
        apiPost(purl.name, purl.json);
    }

    // UNLOAD
    function unload(): void {
        console.log('PURLS STORE: UNLOAD: ' + store.items.length);
        setStore(() => storeDataVM()); // hard reset, do not use draft
    }
};

export default useSavedPurlsStore;
